import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { UPDATE_PHOTOS } from '../api'
import { bulkInputFrom, commonTagIds, tagDelta } from '../lib/bulk'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import type { PhotoUi } from '../lib/permissions'
import type { Photo } from '../types'
import BatchMetadataFields, { BatchChanged, BatchField, BatchMetadata, EMPTY_BATCH_METADATA } from './BatchMetadataFields'

const HIDDEN: BatchField[] = ['description', 'visibility', 'folderId', 'albumId']

interface Props {
  projectId: string
  photos: Photo[]
  ui: PhotoUi
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
}

const BulkEditDialog: React.FC<Props> = ({ projectId, photos, ui, open, onOpenChange, onDone }) => {
  const { t } = useTranslation()
  const common = useMemo(() => commonTagIds(photos.map((p) => p.tags.map((tag) => tag.id))), [photos])
  const [value, setValue] = useState<BatchMetadata>({ ...EMPTY_BATCH_METADATA, tagIds: common })
  const [changed, setChanged] = useState<BatchChanged>({})
  const [updatePhotos, { loading }] = useMutation(UPDATE_PHOTOS, {
    context: { handlesErrors: true },
    refetchQueries: ['photos', 'photoFolders'],
  })

  const anyChange = Object.keys(changed).some((f) => changed[f as BatchField])

  const save = async () => {
    const input = bulkInputFrom(value, changed, tagDelta(common, value.tagIds))
    try {
      await updatePhotos({ variables: { ids: photos.map((p) => p.id), input } })
      toastr.success('', t('Photos_bulk_updated', { count: photos.length }))
      onOpenChange(false)
      onDone()
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Photos_bulk_edit_title', { count: photos.length })}</DialogTitle>
        </DialogHeader>
        <BatchMetadataFields
          projectId={projectId}
          value={value}
          onChange={setValue}
          ui={ui}
          mode="bulk"
          changed={changed}
          onChangedChange={setChanged}
          hide={HIDDEN}
        />
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>{t('Common_cancel')}</Button>
          <Button disabled={loading || !anyChange} onClick={save}>
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BulkEditDialog
